import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DataGrid, GridToolbar, GridPagination } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { baseUrl } from '../Univ.js';

function CollectionAll(props) {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [cashier, setCashier] = useState({ username: '' });
  const [mode, setMode] = useState({ mode: '' });
  const [userData, setUserData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Function to handle start date change
  const handleStartDateChange = (date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  setStartDate(localDate);
};

const handleEndDateChange = (date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  setEndDate(localDate);
};

  // Function to handle cashier selection
  const handleUser = (event) => {
    setCashier({
      ...cashier,
      username: event.target.value
    });
  };

  // Function to handle sales mode selection
  const handleMode = (event) => {
    setMode({
      ...mode,
      mode: event.target.value
    });
  };

  // Effect to fetch user data
  useEffect(() => {
    try {
      axios.get(`${baseUrl}user-search/`)
        .then(function (response) {
          setUserData(response.data);
        });
    } catch (error) {
      console.log(error);
    }
  }, []);

  // Effect to fetch data based on selected filters
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const startDateString = startDate ? startDate.toISOString().substring(0, 10) : '';
    const endDateString = endDate ? endDate.toISOString().substring(0, 10) : '';
    const url = `${baseUrl}all-collection-view/?start_date=${startDateString}&end_date=${endDateString}&cashier=${cashier.username}&mode=${mode.mode}&status=2`;

    try {
      const response = await axios.get(url);
      const data = response.data;

      const formattedData = await Promise.all(data.map(async item => {
        const minidArray = item.min_id ? item.min_id.split(',') : [];
        const itestidArray = item.itest_id ? item.itest_id.split(',') : [];

        const minidRequests = minidArray.map(id => axios.get(`${baseUrl}edit-minor-comp/${id}`));
        const itestidRequests = itestidArray.map(id => axios.get(`${baseUrl}edit-components/${id}`));

        const [minidResponses, itestidResponses] = await Promise.all([
          Promise.all(minidRequests),
          Promise.all(itestidRequests)
        ]);

        const minData = minidResponses.map(response => response.data.cat.cat);
        const invData = itestidResponses.map(response => response.data.cat);

        let audiology = 0;
        let dental = 0;
        let dialysis = 0;
        let minor = 0;
        let ct = 0;
        let xray = 0;
        let scan = 0;

        if (minData.includes('Audiology')) {
          audiology = item.min_paid || 0;
        } else if (minData.includes('Dental')) {
          dental = item.min_paid || 0;
        } else if (minData.includes('Dialysis')) {
          dialysis = item.min_paid || 0;
        } else {
          minor = item.min_paid || 0;
        }

        if (invData.includes('CT')) {
          ct = item.inv_paid || 0;
        } else if (invData.includes('Xray')) {
          xray = item.inv_paid || 0;
        } else if (invData.includes('USG')) {
          scan = item.inv_paid || 0;
        }

        let invoiceLink = '';
        if (item.invoice) {
          invoiceLink = `/op-invoice/${item.invoice}`;
        } else if (item.lab_invoice) {
          invoiceLink = `/lab-invoice-op/${item.lab_invoice}`;
        } else if (item.inv_invoice) {
          invoiceLink = `/inv-invoice-op/${item.inv_invoice}`;
        } else if (item.min_invoice) {
          invoiceLink = `/min-invoice-op/${item.min_invoice}`;
        }

        return {
          ...item,
          fee: item.paid || 0,
          lab: item.lab_paid || 0,
          ct,
          xray,
          scan,
          dialysis,
          audiology,
          dental,
          minor,
          mode: item.mode || item.lab_pmode || item.inv_pmode || item.min_pmode || '',
          invoice: item.invoice || item.lab_invoice || item.inv_invoice || item.min_invoice || '',
          invoiceLink,
          minData,
          invData
        };
      }));

      setCombinedData(formattedData);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, cashier.username, mode.mode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const today = new Date();

  // Columns configuration for the DataGrid
  const columnsInvoiced = [
    { field: 'add_date_date', headerName: 'Date', width: 100 },
    { field: 'appt_id', headerName: 'Appointment ID', width: 120 },
    { field: 'pat__pat_id', headerName: 'Patient ID', width: 80 },
    { field: 'pat__patient', headerName: 'Patient Name', width: 150 },
    { field: 'cons__consultant', headerName: 'Consultant', width: 200 },
    {
      field: 'invoice',
      headerName: 'Invoice No',
      renderCell: (params) => (
        <Link to={params.row.invoiceLink} target="_blank">
          {params.row.invoice}
        </Link>
      ),
      width: 140
    },
    { field: 'fee', headerName: 'Consulting', width: 80 },
    { field: 'lab', headerName: 'Lab', width: 80 },
    { field: 'ct', headerName: 'CT', width: 80 },
    { field: 'xray', headerName: 'Xray', width: 80 },
    { field: 'scan', headerName: 'Scan', width: 80 },
    { field: 'dialysis', headerName: 'Dialysis', width: 80 },
    { field: 'audiology', headerName: 'audiology', width: 80 },
    { field: 'dental', headerName: 'Dental', width: 80 },
    { field: 'minor', headerName: 'Minor', width: 80 },
    { field: 'mode', headerName: 'Mode', width: 80 },
    { field: 'cashier', headerName: 'Cashier', width: 100 },
  ];

  // Function to get row id for DataGrid
  const getRowId = (row) => row.appt_id;

  const Footer = ({ rows }) => {
  let totalFee = 0;
  let totalLab = 0;
  let totalCt = 0;
  let totalXray = 0;
  let totalScan = 0;
  let totalDialysis = 0;
  let totalaudiology = 0;
  let totalDental = 0;
  let totalMinor = 0;
  let totalFeeCash = 0;
  let totalFeeCard = 0;
  let totalFeeUPI = 0;
  rows.forEach((row) => {
    totalFee += Number(row.fee) || 0;
    totalLab += Number(row.lab) || 0;
    totalCt += Number(row.ct) || 0;
    totalXray += Number(row.xray) || 0;
    totalScan += Number(row.scan) || 0;
    totalDialysis += Number(row.dialysis) || 0;
    totalaudiology += Number(row.audiology) || 0;
    totalDental += Number(row.dental) || 0;
    totalMinor += Number(row.minor) || 0;
    // Calculate payment mode totals
    if (row.mode === 'cash') {
      totalFeeCash += Number(row.fee) + Number(row.lab) + Number(row.ct) + Number(row.xray) + Number(row.scan) + Number(row.dialysis) + Number(row.audiology) + Number(row.dental) + Number(row.minor) || 0;
    } else if (row.mode === 'card') {
      totalFeeCard += Number(row.fee) + Number(row.lab) + Number(row.ct) + Number(row.xray) + Number(row.scan) + Number(row.dialysis) + Number(row.audiology) + Number(row.dental) + Number(row.minor) || 0;
    } else if (row.mode === 'UPI') {
      totalFeeUPI += Number(row.fee) + Number(row.lab) + Number(row.ct) + Number(row.xray) + Number(row.scan) + Number(row.dialysis) + Number(row.audiology) + Number(row.dental) + Number(row.minor) || 0;
    }
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '2px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <div>Total Consulting: {totalFee}</div>
        <div>Total Lab: {totalLab}</div>
        <div>Total CT: {totalCt}</div>
        <div>Total Xray: {totalXray}</div>
        <div>Total Scan: {totalScan}</div>
        <div>Total Dialysis: {totalDialysis}</div>
        <div>Total Audiology: {totalaudiology}</div>
        <div>Total Dental: {totalDental}</div>
        <div>Total Minor: {totalMinor}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-evenly', marginTop: '8px' }}>
        <div>Cash Total: {totalFeeCash.toFixed(2)}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <div>Card Total: {totalFeeCard.toFixed(2)}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <div>UPI Total: {totalFeeUPI.toFixed(2)}</div>
      </div>

    </div>
  );
};

 return (
    <section className="container mt-4">
      <h3 className="text-center" style={{ color: 'black', backgroundColor: '#e5eaef' }}>
        <i className="fa-solid fa-calendar-days" aria-hidden="true" style={{ color: 'red' }}></i> All Collection Report
      </h3>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label className="col-sm-3 col-lg-2 control-label">Select Date</label>
            <div className="col-sm-9 col-lg-10 controls">
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={today}
                placeholderText={'Please select From date'}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
              />
            </div>
            <div className="col-sm-9 col-lg-10 controls">
              <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={today}
                placeholderText={'Please select To date'}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
              />
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-4 mt-4">
            <label htmlFor="user" className="form-label">Search by Cashier</label>
            <select name="user" id="username" value={cashier.user} className="form-control"
              onChange={(event) => handleUser(event)}>
              <option value="">Select Cashier</option>
              {userData.map((User, index) => (
                <option key={index} value={User.username}>{User.username}</option>
              ))}
            </select>
          </div>

          <div className="mb-4 mt-4 form-control">
            <label htmlFor="mode" className="form-label">Search by Sales Mode</label>
            <select name="mode" id="mode" value={mode.mode} className="form-control"
              onChange={(event) => handleMode(event)}>
              <option value="">Select Sales Mode</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="creditpay">Credit Payment</option>
              <option value="UPI">UPI</option>
            </select>
          </div>
        </div>
      </div>

      {/* Render DataGrid only if data is loaded and available */}
      {!isLoading && combinedData.length > 0 && (
        <div>
          <h3 className="text-center" style={{ color: 'black', backgroundColor: '#e5eaef' }}>
            <i className="fa-solid fa-list" aria-hidden="true" style={{ color: 'red' }}></i> Collection Report
          </h3>
          <div style={{ height: "100%", width: '100%' }}>
            <DataGrid
              sx={{
                boxShadow: 4,
                border: 4,
                borderColor: 'primary.light',
                '& .MuiDataGrid-cell:hover': {
                  color: 'primary.main',
                },
              }}
              getRowId={getRowId}
              rows={combinedData.filter(payment => (payment.paid > 0 || payment.lab_paid > 0 || payment.inv_paid > 0 || payment.min_paid > 0))}
              columns={columnsInvoiced}
              pageSize={1000}
              pagination
              slots={{
                toolbar: GridToolbar,
                Pagination: GridPagination,

              }}
              autoHeight
            />
            <table className="mt-4" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tfoot>
              <Footer rows={combinedData.filter(payment => (payment.paid > 0 || payment.lab_paid > 0 || payment.inv_paid > 0 || payment.min_paid > 0))} />
            </tfoot>
          </table>
          </div>
        </div>
      )}
    </section>
  )
}

export default CollectionAll;
